import React, { useEffect, useState, useCallback } from 'react';
import { Table, Button, Container, Row, Col } from 'react-bootstrap';
import { getUsers, deleteUser, patchUser, unblockUser, logError } from '../api';
import { useNavigate } from 'react-router-dom';
import DeleteConfirmationModal from './Global/DeleteConfirmationModal';
import DisabledToggle from './ui/DisabledToggle';
import DisabledRowStyles from './ui/DisabledRowStyles';
import SortableHeader from './ui/SortableHeader';
import { useDisabledToggle } from '../hooks/useDisabledToggle';
import { useSorting } from '../hooks/useSorting';
import { getDisabledRowClass, createToggleButton } from '../utils/disabledUtils';
import { isAdmin, isPropietario } from '../services/AuthService';

const UserList = () => {
    const [users, setUsers] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const { includeDisabled, setIncludeDisabled } = useDisabledToggle();
    const { orderBy, orderDesc, handleSort, getSortIcon } = useSorting();
    const navigate = useNavigate();

    const loadUsers = useCallback(async () => {
        try {
            const data = await getUsers(includeDisabled, orderBy, orderDesc);
            setUsers(data);
        } catch (error) {
            logError(error);
            console.error('Error loading users:', error);
        }
    }, [includeDisabled, orderBy, orderDesc]);

    useEffect(() => {
        loadUsers();
    }, [loadUsers]);

    const confirmDeleteUser = (id) => {
        setUserToDelete(id);
        setShowModal(true);
    };

    const handleDeleteUser = async () => {
        try {
            await deleteUser(userToDelete);
            loadUsers();
            setShowModal(false);
        } catch (error) {
            logError(error);
            console.error('Error deleting user:', error);
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setUserToDelete(null);
    };

    const handleEditUser = (id) => {
        navigate(`/users/edit/${id}`);
    };

    const handleCreateUser = () => {
        navigate('/users/new');
    };

    const handleToggleUserStatus = async (id, currentStatus) => {
        try {
            await patchUser(id, { id, disabled: !currentStatus });
            loadUsers();
        } catch (error) {
            logError(error);
            console.error('Error toggling user status:', error);
        }
    };

    const handleUnblockUser = async (id) => {
        try {
            await unblockUser(id);
            loadUsers();
        } catch (error) {
            logError(error);
            console.error('Error unblocking user:', error);
        }
    };

    const canModifyUser = (user) => {
        const targetIsAdmin = user.roles.some((role) => role.name === 'Admin');
        const targetIsPropietario = user.roles.some((role) => role.name === 'Propietario');

        if (isAdmin()) return true;
        if (isPropietario()) return !targetIsAdmin;
        return !targetIsAdmin && !targetIsPropietario;
    };

    return (
        <>
            <DisabledRowStyles />
            <Container fluid className="my-4">
                <Row className="mb-4">
                    <Col xs={12} md={6}>
                        <h1>Usuarios</h1>
                    </Col>
                    <Col xs={12} md={6} className="d-flex justify-content-end align-items-center">
                        <DisabledToggle 
                            includeDisabled={includeDisabled}
                            onChange={setIncludeDisabled}
                        />
                        <Button variant="primary" onClick={handleCreateUser} data-testid="new-user-button">
                            Nuevo usuario
                        </Button>
                    </Col>
                </Row>

                <Table striped bordered hover>
                <thead>
                    <tr>
                        <SortableHeader column="id" onSort={handleSort} getSortIcon={getSortIcon}>Id</SortableHeader>
                        <SortableHeader column="username" onSort={handleSort} getSortIcon={getSortIcon}>Username</SortableHeader>
                        <th>Roles</th>
                        <th>Acciones</th>
                    </tr>
                </thead>
                <tbody>
                    {users.map((user) => (
                        <tr key={user.id} className={getDisabledRowClass(user.disabled)} data-testid={`user-row-${user.username}`}>
                            <td>{user.id}</td>
                            <td data-testid={`username-${user.id}`}>{user.username}</td>
                            <td>
                                {Array.isArray(user.roles)
                                    ? user.roles.map((role, index) => (
                                        <div key={index}>{role.name || role}</div>
                                    ))
                                    : 'Ningún rol asignado'}
                            </td>
                            <td>
                                {canModifyUser(user) ? (
                                    <>
                                        {user.isBlocked ? (
                                            <Button
                                                variant="info"
                                                onClick={() => handleUnblockUser(user.id)}
                                            >
                                                Desbloquear
                                            </Button>
                                        ) : (
                                            <>
                                                <Button
                                                    variant="warning"
                                                    onClick={() => handleEditUser(user.id)}
                                                    data-testid={`edit-user-${user.id}`}
                                                >
                                                    Editar
                                                </Button>
                                                <Button
                                                    variant={user.disabled ? "success" : "secondary"}
                                                    onClick={() => handleToggleUserStatus(user.id, user.disabled)}
                                                    size="sm"
                                                    className="me-2"
                                                    data-testid={`toggle-user-${user.id}`}
                                                >
                                                    {user.disabled ? "Habilitar" : "Deshabilitar"}
                                                </Button>
                                                <Button
                                                    variant="danger"
                                                    onClick={() => confirmDeleteUser(user.id)}
                                                >
                                                    Borrar
                                                </Button>
                                            </>
                                        )}
                                    </>
                                ) : (
                                    <span>Sin permisos</span>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
                </Table>

                <DeleteConfirmationModal
                    show={showModal}
                    onHide={handleCloseModal}
                    onConfirm={handleDeleteUser}
                    message="¿Estás seguro de que deseas eliminar este usuario? Esta acción no se puede deshacer."
                />
            </Container>
        </>
    );
};

export default UserList;
